/* =========================================================
   PLINTH VARIANTS — robust dependency (keyword match)
   File: plinth-variants.js

   Rule:
     If Form contains "cyl" => only allow Size containing "reg"
     Else => allow all sizes
   ========================================================= */

(() => {
  const HANDLE = "plinth";

  const norm = (s) => (s ?? "").toString().trim().replace(/\s+/g, " ").toLowerCase();

  function getPicker() {
    return (
      document.querySelector(`variant-picker[data-product-url="/products/${HANDLE}"][ref="mainVariantPicker"]`) ||
      document.querySelector(`variant-picker[data-product-url="/products/${HANDLE}"]`) ||
      document.querySelector("variant-picker")
    );
  }

  function getFieldsets(root) {
    return Array.from(root?.querySelectorAll('fieldset[data-fieldset-index]') || []);
  }

  function getLegend(fs) {
    return norm(fs?.querySelector("legend")?.innerText || "");
  }

  function getCheckedValue(fs) {
    const el = fs?.querySelector('input[type="radio"]:checked');
    return el ? norm(el.value) : "";
  }

  function getInputs(fs) {
    return Array.from(fs?.querySelectorAll('label.variant-option__button-label > input[type="radio"]') || []);
  }

  function setVisible(input, visible) {
    const label = input.closest("label.variant-option__button-label");
    if (!label) return;

    if (visible) {
      label.hidden = false;
      label.style.removeProperty("display");
      label.style.removeProperty("pointer-events");
      label.style.removeProperty("visibility");
      label.style.removeProperty("position");
      label.style.removeProperty("left");
    } else {
      label.hidden = true;
      label.style.setProperty("display", "none", "important");
      label.style.setProperty("pointer-events", "none", "important");
      label.style.setProperty("visibility", "hidden", "important");
      label.style.setProperty("position", "absolute", "important");
      label.style.setProperty("left", "-99999px", "important");
    }
  }

  function setDisabled(input, disabled) {
    input.disabled = disabled;
    if (disabled) input.setAttribute("aria-disabled", "true");
    else input.removeAttribute("aria-disabled");
  }

  function clickRadio(input) {
    if (!input || input.disabled) return;
    input.checked = true;
    input.dispatchEvent(new Event("input", { bubbles: true }));
    input.dispatchEvent(new Event("change", { bubbles: true }));
  }

  function findFieldsetByLegend(root, keyword) {
    const fss = getFieldsets(root);
    return fss.find(fs => getLegend(fs).includes(keyword)) || null;
  }

  function apply(root) {
    // Prefer matching by legend text first (more stable)
    const fsForm = findFieldsetByLegend(root, "form") || root.querySelector('fieldset[data-fieldset-index="0"]');
    const fsSize = findFieldsetByLegend(root, "size") || root.querySelector('fieldset[data-fieldset-index="1"]');
    if (!fsForm || !fsSize) return;

    const formVal = getCheckedValue(fsForm);
    if (!formVal) return;

    const sizeInputs = getInputs(fsSize);
    if (!sizeInputs.length) return;

    const isCylinder = formVal.includes("cyl"); // cylinder / cylindrical / cyl.
    for (const input of sizeInputs) {
      const v = norm(input.value);
      const ok = !isCylinder || v.includes("reg"); // cylinder => only regular
      setDisabled(input, !ok);
      setVisible(input, ok);
    }

    const checked = fsSize.querySelector('input[type="radio"]:checked');
    if (!checked || checked.disabled) {
      const first = sizeInputs.find(i => !i.disabled);
      if (first) clickRadio(first);
    }
  }

  function init() {
    if (!location.pathname.includes(`/products/${HANDLE}`)) return;

    let applying = false;
    let scheduled = false;

    const run = () => {
      if (scheduled) return;
      scheduled = true;

      requestAnimationFrame(() => {
        scheduled = false;

        const root = getPicker();
        if (!root) return;

        if (applying) return;
        applying = true;
        try {
          apply(root);
        } finally {
          applying = false;
        }

        setTimeout(() => {
          const r = getPicker();
          if (r) apply(r);
        }, 60);
      });
    };

    run();

    document.addEventListener("change", (e) => {
      if (e.target && e.target.matches('input[type="radio"]')) run();
    });
    document.addEventListener("input", (e) => {
      if (e.target && e.target.matches('input[type="radio"]')) run();
    });

    window.addEventListener("popstate", run);

    const bodyObserver = new MutationObserver(() => run());
    bodyObserver.observe(document.body, { childList: true, subtree: true });
  }

  document.addEventListener("DOMContentLoaded", init);
})();
